import { match, P } from "ts-pattern";
import { InclusionCode } from "@silversea-ssc/web-inclusions";
import {
  InclusionContent,
  FallbackInclusionConfigOnly,
  FallbackInclusionConfig,
  FallbackInclusionConfigExcept,
  InfographicConfig,
  StepConfig,
  StepsGroup,
  Step
} from "./types";
import { isPresent } from "../utils/checks";
import { IconsId } from "@silversea-ssc/design-system-icons";

export const isActiveInclusion =
  (activeCodes: InclusionCode[]) =>
  (code: InclusionCode): boolean => {
    return activeCodes.includes(code);
  };

export const inclusionContentForCode = (
  code: InclusionCode,
  inclusionsContent: InclusionContent[]
): InclusionContent | undefined => {
  return inclusionsContent.find((x) => x.codes.includes(code));
};

const anyInclusionCodeIsActive = (
  inclusionCodes: InclusionCode[],
  activeInclusionCodes: InclusionCode[]
): boolean =>
  (inclusionCodes || []).some((c) => c && activeInclusionCodes.includes(c));

const fallbackInclusionCodeOnly =
  (activeInclusionCodes: InclusionCode[]) =>
  ({ onlyWhenActiveCodes, code }: FallbackInclusionConfigOnly) =>
    anyInclusionCodeIsActive(onlyWhenActiveCodes, activeInclusionCodes)
      ? code
      : undefined;

const fallbackInclusionCodeExcept =
  (activeInclusionCodes: InclusionCode[]) =>
  ({ exceptWhenActiveCodes, code }: FallbackInclusionConfigExcept) =>
    anyInclusionCodeIsActive(exceptWhenActiveCodes, activeInclusionCodes)
      ? undefined
      : code;

export const maybeFallbackInclusionCode = (
  fallbackConfig: FallbackInclusionConfig | undefined,
  activeInclusionCodes: InclusionCode[]
): InclusionCode | undefined =>
  match(fallbackConfig)
    .with(undefined, () => undefined)
    .with(P.string, (code) => code)
    .with({ type: "only" }, fallbackInclusionCodeOnly(activeInclusionCodes))
    .with({ type: "except" }, fallbackInclusionCodeExcept(activeInclusionCodes))
    .exhaustive();

function stepIcon(
  stepConfig: StepConfig,
  activeInclusions: InclusionContent[]
): IconsId {
  const overrideIcon = stepConfig.overrideIcon;
  return match(overrideIcon)
    .with(undefined, () => activeInclusions[0].icon)
    .with({ strategy: "all-active" }, (strategy) => {
      if (activeInclusions.length == stepConfig.inclusions.length)
        return strategy.icon;

      return activeInclusions[0].icon;
    })
    .with({ strategy: "all-active-inclusions" }, (strategy) => {
      const activeInclusionCodes = activeInclusions.flatMap((i) => i.codes);
      const allActiveInclusionsRequired = strategy.inclusions.every((code) =>
        activeInclusionCodes.includes(code)
      );

      if (allActiveInclusionsRequired) return strategy.icon;

      return activeInclusions[0].icon;
    })
    .with(P.string, (icon) => icon)
    .exhaustive();
}

export const computeStep = (
  stepConfig: StepConfig,
  activeInclusionCodes: InclusionCode[],
  inclusionsContent: InclusionContent[],
  groupId: string
): Step | undefined => {
  const stepActiveInclusionCodes = stepConfig.inclusions.filter(
    isActiveInclusion(activeInclusionCodes)
  );

  const fallbackInclusionCode = maybeFallbackInclusionCode(
    stepConfig.fallbackInclusion,
    activeInclusionCodes
  );
  if (
    stepActiveInclusionCodes.length == 0 &&
    isPresent(fallbackInclusionCode)
  ) {
    const fallbackInclusionContent = inclusionContentForCode(
      fallbackInclusionCode,
      inclusionsContent
    );
    if (!fallbackInclusionContent) return undefined;

    return {
      id: stepConfig.id,
      groupId,
      type: "disabled",
      icon: stepIcon(stepConfig, [fallbackInclusionContent]),
      inclusions: [fallbackInclusionContent]
    };
  }

  const activeInclusionsContent = stepActiveInclusionCodes
    .map((c) => inclusionContentForCode(c, inclusionsContent))
    .filter(isPresent);

  const firstActiveInclusion = activeInclusionsContent[0];
  if (!firstActiveInclusion) return undefined;

  return {
    id: stepConfig.id,
    groupId,
    type: "active",
    inclusions: activeInclusionsContent,
    icon: stepIcon(stepConfig, activeInclusionsContent)
  };
};

export const computeInfographic = (
  infographicConfig: InfographicConfig,
  activeInclusionCodes: InclusionCode[],
  inclusionsContent: InclusionContent[],
  groupTitles?: { [id: StepConfig["id"]]: string | undefined }
): StepsGroup[] => {
  return infographicConfig.groups
    .map((g) => {
      const steps = g.steps
        .map((stepConfig) => {
          return computeStep(
            stepConfig,
            activeInclusionCodes,
            inclusionsContent,
            g.id
          );
        })
        .filter(isPresent);

      return {
        id: g.id,
        icon: g.icon,
        title: groupTitles?.[g.id] || g.id,
        steps
      };
    })

    .filter(isPresent);
};
